import { useContext } from 'react';
import { AuthContext } from '../../providers/AuthProvider';
import { ApiMethod } from '../models/api-types.type';
import { routes } from '../../assets/constants';
import { VendorInvoice, WireBankDetails, StripeInvoiceSummary } from '../models/billing.model';
import { downloadPdf, fetchPdfObjectUrl } from '../utils/download-file.util';

/**
 * Stripe billing endpoints. Both return `{ url }` — the caller redirects the
 * browser there (hosted Checkout for a new subscription, Billing Portal to
 * manage an existing one).
 */
export const useBillingApi = () => {
  const { sendAuthGuardedRequest } = useContext(AuthContext);

  const createCheckoutSession = (planId: string): Promise<{ url: string }> =>
    sendAuthGuardedRequest(ApiMethod.POST, routes.billing.checkoutSession, { planId }) as Promise<{ url: string }>;

  // Embedded checkout: returns a client_secret that Stripe's EmbeddedCheckout
  // mounts on our own page (no redirect to Stripe's domain).
  const createEmbeddedCheckoutSession = (planId: string, interval: 'month' | 'year' = 'month'): Promise<{ clientSecret: string }> =>
    sendAuthGuardedRequest(ApiMethod.POST, routes.billing.embeddedCheckout, { planId, interval }) as Promise<{ clientSecret: string }>;

  // Embedded checkout for an accepted quote's custom Stripe price.
  const createQuoteCheckoutSession = (quoteId: string): Promise<{ clientSecret: string }> =>
    sendAuthGuardedRequest(ApiMethod.POST, routes.billing.quoteCheckout, { quoteId }) as Promise<{ clientSecret: string }>;

  const getBillingConfig = (): Promise<{ publishableKey: string; cardFeePercent: number; annualDiscountPercent: number }> =>
    sendAuthGuardedRequest(ApiMethod.GET, routes.billing.config) as Promise<{ publishableKey: string; cardFeePercent: number; annualDiscountPercent: number }>;

  const createPortalSession = (): Promise<{ url: string }> =>
    sendAuthGuardedRequest(ApiMethod.POST, routes.billing.portalSession) as Promise<{ url: string }>;

  // ── Wire-transfer (manual invoice) ────────────────────────────────────────

  // Create a wire invoice for a paid plan. This does NOT change the vendor's
  // plan — it stays on its current/free plan until a super-admin confirms payment.
  const createWireInvoice = (planId: string, interval: 'month' | 'year'): Promise<VendorInvoice> =>
    sendAuthGuardedRequest(ApiMethod.POST, routes.billing.wireInvoice, { planId, interval }) as Promise<VendorInvoice>;

  const getMyInvoices = (): Promise<VendorInvoice[]> =>
    sendAuthGuardedRequest(ApiMethod.GET, routes.billing.myInvoices) as Promise<VendorInvoice[]>;

  const getMyStripeInvoices = (): Promise<StripeInvoiceSummary[]> =>
    sendAuthGuardedRequest(ApiMethod.GET, routes.billing.myStripeInvoices) as Promise<StripeInvoiceSummary[]>;

  /** Super-admin: set a wire vendor's grandfathered renewal price (omit amount to reset to the current plan price). */
  const setVendorWirePrice = (vendorId: string, amount?: number): Promise<{ wirePriceAmount: number | null }> =>
    sendAuthGuardedRequest(ApiMethod.PATCH, routes.billing.adminVendorWirePrice(vendorId), { amount }) as Promise<{ wirePriceAmount: number | null }>;

  /** Super-admin: move a vendor's Stripe subscription onto the plan's current price (prorate = charge the difference now). */
  const repriceVendorStripe = (vendorId: string, prorate: boolean): Promise<{ subscriptionId: string }> =>
    sendAuthGuardedRequest(ApiMethod.PATCH, routes.billing.adminVendorStripeReprice(vendorId), { prorate }) as Promise<{ subscriptionId: string }>;

  const getWireConfig = (): Promise<{ bankDetails: WireBankDetails }> =>
    sendAuthGuardedRequest(ApiMethod.GET, routes.billing.wireConfig) as Promise<{ bankDetails: WireBankDetails }>;

  const downloadInvoicePdf = (invoiceId: string, invoiceNumber: string): Promise<void> =>
    downloadPdf(routes.billing.invoicePdf(invoiceId), invoiceNumber);

  // Returns a blob object URL for inline viewing (no download). Revoke when done.
  const viewInvoicePdf = (invoiceId: string): Promise<string> =>
    fetchPdfObjectUrl(routes.billing.invoicePdf(invoiceId));

  return {
    createCheckoutSession,
    createEmbeddedCheckoutSession,
    createQuoteCheckoutSession,
    getBillingConfig,
    createPortalSession,
    createWireInvoice,
    getMyInvoices,
    getMyStripeInvoices,
    setVendorWirePrice,
    repriceVendorStripe,
    getWireConfig,
    downloadInvoicePdf,
    viewInvoicePdf,
  };
};
